import React, { useContext, useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { ConfirmationModal, ModalActionTypes } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useQuery } from '@apollo/react-hooks'
import { compose } from 'recompose'

import { MOC_ONBOARDING_FLOW, USER_UUID } from 'app-constants'
import { Accessibility } from 'components/Accessibility'
import CenteredSpinner from 'components/CenteredSpinner'
import { ActionType, FunnelName, PageName, User } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import theme from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { referralCaptureMOCInterest } from 'utils/Optimizely/features'
import { IMPACT_RADIUS_SOURCE, REFERRALS_SOURCE } from 'utils/sourceAttributes'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'

import MocInterstitialPersonImage from '../assets/moc-success-person.svg'
import {
  IntentOptInEvent,
  IntentOptOutEvent,
  IntentScreenViewedEvent,
  MoreInfoButtonTappedEvent,
  MoreInfoContainerViewedEvent,
  MoreInfoOkButtonTappedEvent,
  NextButtonTappedEvent,
  ScreenViewedEvent,
  SkipButtonTappedEvent,
} from './events'
import SourceIdQuery from './source.query.gql'
import {
  ActionWrapper,
  Background,
  Container,
  Eyebrow,
  EyebrowText,
  Green,
  Link,
  ModalContainer,
  ModalText,
  NavBar,
  StyledBody,
  StyledButton,
  StyledFootnote,
  StyledImage,
  StyledLayout,
  StyledTextButton,
  StyledTitle,
  WhiteIcon,
} from './styled-components'

type Props = {
  ctaLabel: string
  handleNextClick: () => void
  header: string
  text: string
  secondaryCtaLabel: string
} & WithTieredRegistrationProps &
  WithFlowRouterProps &
  WithOptimizelyProps

const isOnlyCaptureInterest = (isFeatureEnabled, userData): boolean => {
  const enabled = isFeatureEnabled(referralCaptureMOCInterest)
  if (!enabled || !userData) return false
  return (
    userData.user.sourceId === REFERRALS_SOURCE || userData.user.sourceId === IMPACT_RADIUS_SOURCE
  )
}

const enhance = compose<Props, {}>(withFlowRouter, withTieredRegistration, withOptimizely)

const MOCInterstitial: React.FunctionComponent<Props> = ({ route, setFlow, isFeatureEnabled }) => {
  const { isDynamicFunnelEnabled, routeToPage, updateAttribution } = useContext(FunnelContext)
  const { getRemoteString } = useRemoteStrings()
  const { data, loading } = useQuery<{ user: User }>(SourceIdQuery)
  const isCaptureInterestEnabled = isOnlyCaptureInterest(isFeatureEnabled, data)
  const [isShowingModal, setIsShowingModal] = useState<boolean>(false)
  const isDesktop = useMediaQuery({ minWidth: `${theme.breakpoints.md}px` })
  const isMobile = useMediaQuery({ maxWidth: `${theme.breakpoints.sm}px` })
  const uuid = window.sessionStorage.getItem(USER_UUID)
  const modalText = (
    <ModalContainer>
      <ModalText>
        {getRemoteString({ key: 'registration.moc_tier.interstitial.modal_body_1' })}
      </ModalText>
      <ModalText>
        {getRemoteString({ key: 'registration.moc_tier.interstitial.modal_body_2' })}
      </ModalText>
    </ModalContainer>
  )
  const primaryCta = getRemoteString({
    key: isCaptureInterestEnabled
      ? 'registration.moc_intent.cta.primary'
      : 'registration.moc_tier.interstitial.next_button',
  })
  const secondaryCta = getRemoteString({
    key: isCaptureInterestEnabled
      ? 'registration.moc_intent.cta.secondary'
      : 'registration.moc_tier.interstitial.skip_button',
  })

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: isCaptureInterestEnabled ? IntentScreenViewedEvent : ScreenViewedEvent,
      flow: getFlow(),
    })
  }, [isCaptureInterestEnabled])

  const handleOpenModal = () => {
    trackEvent('Button Tapped', {
      eventData: MoreInfoButtonTappedEvent,
      flow: getFlow(),
    })
    setIsShowingModal(true)
  }

  const handleCloseModal = () => {
    trackEvent('Button Tapped', {
      eventData: MoreInfoOkButtonTappedEvent,
      flow: getFlow(),
    })
    setIsShowingModal(false)
  }

  const handleContinue = async () => {
    window.analytics.identify(uuid, {
      mighty_oak_card_onboarding_opt_in: true,
    })
    trackEvent('Button Tapped', {
      eventData: isCaptureInterestEnabled ? IntentOptInEvent(primaryCta) : NextButtonTappedEvent,
      flow: getFlow(),
    })

    if (!isCaptureInterestEnabled) {
      await updateAttribution(FunnelName.MightyOakCard)
      await setFlow(MOC_ONBOARDING_FLOW)
    }

    isDynamicFunnelEnabled
      ? routeToPage(PageName.MocRoutingInterstitial, ActionType.Continue)
      : route({ from: FlowRoute.MOCInterstitial, action: 'continue' })
  }

  const handleSkip = async (shouldIdentify: boolean) => {
    // Product has decided that clicking the X icon in the navbar indicates that a user is declining to provide
    // an answer to opting into the flow; the functionality remains the same in terms of routing but the user
    // should not be marked with the opt in attribute unless the user "provides an answer"
    if (shouldIdentify) {
      window.analytics.identify(uuid, {
        mighty_oak_card_onboarding_opt_in: false,
      })
    }

    trackEvent('Button Tapped', {
      eventData: isCaptureInterestEnabled ? IntentOptOutEvent(secondaryCta) : SkipButtonTappedEvent,
      flow: getFlow(),
    })

    isDynamicFunnelEnabled
      ? routeToPage(PageName.MocRoutingInterstitial, ActionType.Continue)
      : route({ from: FlowRoute.MOCInterstitial, action: 'skip' })
  }

  if (loading) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Background>
        <NavBar isDesktop={isDesktop}>
          <WhiteIcon
            icon={AcornsIcon.Icon.CloseXIconWhite}
            width={16}
            onClick={() => handleSkip(false)}
          />
          {!isMobile && <span>Mighty Oak Card</span>}
          <div />
        </NavBar>
        <Accessibility logoColor="white" />

        <StyledLayout>
          <Container>
            <Eyebrow>
              <WhiteIcon icon={AcornsIcon.Icon.UtilityNew} width={22} />{' '}
              <EyebrowText>
                {getRemoteString({
                  key: 'registration.moc_tier.interstitial.header.notification',
                })}
              </EyebrowText>
            </Eyebrow>
            <StyledTitle data-testid={TestIDs.MOC_INTERSTITIAL.TITLE}>
              The <Green>Mighty Oak</Green> debit card
            </StyledTitle>
            <StyledBody>
              {getRemoteString({ key: 'registration.moc_tier.interstitial.header.body' })}
            </StyledBody>
            {!isCaptureInterestEnabled && (
              <StyledFootnote>
                For more information on pricing{' '}
                <Link
                  data-testid={TestIDs.MOC_INTERSTITIAL.MORE_INFO_BUTTON}
                  onClick={handleOpenModal}
                >
                  tap here.
                </Link>
              </StyledFootnote>
            )}
          </Container>
          <StyledImage
            src={MocInterstitialPersonImage}
            data-testid={TestIDs.MOC_INTERSTITIAL.IMAGE}
          />
          <ActionWrapper>
            <StyledButton
              data-testid={TestIDs.MOC_INTERSTITIAL.NEXT_BUTTON}
              onPress={handleContinue}
            >
              {primaryCta}
            </StyledButton>

            <StyledTextButton
              data-testid={TestIDs.MOC_INTERSTITIAL.SKIP_BUTTON}
              onPress={() => handleSkip(true)}
              styleType="flat"
            >
              {secondaryCta}
            </StyledTextButton>
          </ActionWrapper>
        </StyledLayout>
      </Background>

      {!isCaptureInterestEnabled && (
        <ConfirmationModal
          onOpenComplete={() =>
            trackEvent('Container Viewed', {
              eventData: MoreInfoContainerViewedEvent,
              flow: getFlow(),
            })
          }
          isOpen={isShowingModal}
          onRequestClose={handleCloseModal}
          actions={[
            {
              label: getRemoteString({
                key: 'registration.moc_tier.interstitial.modal_primary_action_label',
              }),
              type: ModalActionTypes.primary,
              handler: handleCloseModal,
            },
          ]}
          message={modalText}
          title={getRemoteString({ key: 'registration.moc_tier.interstitial.modal_title' })}
        />
      )}
    </>
  )
}

export default enhance(MOCInterstitial)
